import { useRef, useState } from "react";
import { Play, Pause } from "lucide-react";
import { IMAGES, VIDEO_URL } from "../../lib/site";

export const VideoSection = () => {
    const videoRef = useRef(null);
    const [playing, setPlaying] = useState(false);
    const [hasError, setHasError] = useState(false);

    const toggle = () => {
        const v = videoRef.current;
        if (!v || hasError) return;
        if (v.paused) {
            v.play().then(() => setPlaying(true)).catch(() => setHasError(true));
        } else {
            v.pause();
            setPlaying(false);
        }
    };

    return (
        <section
            id="video"
            data-testid="video-section"
            className="py-24 lg:py-32 bg-[#F5F4F2] relative"
        >
            <div className="max-w-6xl mx-auto px-6 lg:px-10">
                <div className="max-w-2xl mb-12 reveal">
                    <p className="overline mb-5">Uma mensagem da Luara</p>
                    <h2 className="font-display text-4xl sm:text-5xl lg:text-[3.5rem] leading-[1.08] text-[#2F2F2F]">
                        Às vezes a vida parece um <em className="text-[#9f1e1e] not-italic font-display italic">quebra-cabeça</em> com peças que não se encaixam.
                    </h2>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
                    <div className="lg:col-span-8 reveal reveal-delay-1">
                        <div className="video-shell relative aspect-video bg-[#2F2F2F]">
                            <video
                                ref={videoRef}
                                src={VIDEO_URL}
                                poster={IMAGES.process}
                                className="w-full h-full object-cover"
                                playsInline
                                preload="metadata"
                                onEnded={() => setPlaying(false)}
                                onError={() => setHasError(true)}
                                data-testid="emotional-video"
                            >
                                <source src={VIDEO_URL} type="video/mp4" />
                            </video>
                            {hasError && (
                                <div className="absolute inset-0 flex items-center justify-center bg-[#2F2F2F]/85 text-[#F5F4F2] text-center px-8">
                                    <p className="font-display italic text-xl leading-snug max-w-md">
                                        “Você merece viver com mais conexão e propósito. Vamos construir, juntos, um novo capítulo na sua vida.”
                                    </p>
                                </div>
                            )}
                            <button
                                type="button"
                                onClick={toggle}
                                aria-label={playing ? "Pausar vídeo" : "Reproduzir vídeo"}
                                data-testid="video-play-button"
                                className={`absolute inset-0 flex items-center justify-center transition-opacity duration-500 ${
                                    hasError ? "hidden" : playing ? "opacity-0 hover:opacity-100" : "opacity-100"
                                }`}
                                style={{
                                    background: playing
                                        ? "transparent"
                                        : "linear-gradient(180deg, rgba(47,47,47,0.0) 30%, rgba(47,47,47,0.55) 100%)",
                                }}
                            >
                                <span className="flex items-center justify-center w-20 h-20 rounded-full bg-[#F5F4F2]/95 text-[#9f1e1e] shadow-2xl hover:scale-110 transition-transform duration-500">
                                    {playing ? (
                                        <Pause className="w-7 h-7" strokeWidth={1.5} />
                                    ) : (
                                        <Play className="w-7 h-7 ml-1" strokeWidth={1.5} />
                                    )}
                                </span>
                            </button>
                        </div>
                    </div>

                    <div className="lg:col-span-4 reveal reveal-delay-2">
                        <p className="font-display italic text-2xl lg:text-[1.7rem] leading-snug text-[#2F2F2F]/85">
                            “A psicoterapia é um espaço para você entender o que está acontecendo, transformar padrões
                            que não fazem mais sentido, e buscar caminhos mais leves.”
                        </p>
                        <div className="mt-6 flex items-center gap-3">
                            <div className="w-10 h-px bg-[#9f1e1e]" />
                            <span className="overline">Luara Ainá</span>
                        </div>
                        <p className="mt-8 text-base leading-relaxed text-[#2F2F2F]/70">
                            Você não precisa atravessar esse momento sozinho. Vamos construir, juntos, um novo
                            capítulo na sua história.
                        </p>
                    </div>
                </div>
            </div>
        </section>
    );
};
